import { useIntl } from 'react-intl'

import { ButtonGroup, FormattedMessage } from '@subaio/components'

import type { OnLogEvent } from 'lib/container'

import type {
	Handlers,
	OpportunityCarInsuranceStep,
	OpportunityCarInsuranceStepType,
} from 'containers/OpportunityCarInsurance/handlers'

import { ButtonLink, ButtonSolid, ButtonText } from 'components/Buttons'
import { Card } from 'components/Card'
import { HeaderIconSuccess, IconCancel, IconLink, IconPrivacy } from 'components/Icons'
import { Header, HeaderIconWrapper, PointItem, PointList } from 'components/Layout'

import { Title } from '../Title'

type Props = TaggedType<OpportunityCarInsuranceStep, OpportunityCarInsuranceStepType.Consent> & {
	onShowComparison: Handlers['onShowComparison']
	onShowDataProcessing: Handlers['onShowDataProcessing']
	onOpenUrl: (url: string) => void
	onLogScopedEvent: OnLogEvent
	onStepBack?: () => void
}

export const Consent = ({
	selectedInsurer,
	personalDetails,
	licensePlate,
	damageFreeYears,
	drivenDistancePerYear,
	currentCoverage,
	onShowComparison,
	onShowDataProcessing,
	onOpenUrl,
	onLogScopedEvent,
	onStepBack,
}: Props) => {
	const { formatMessage } = useIntl()

	return (
		<Card>
			<Header>
				<HeaderIconWrapper>
					<HeaderIconSuccess />
				</HeaderIconWrapper>
				<Title>
					<FormattedMessage
						id='opportunityCarInsurance.consent.title'
						defaultMessage='For the comparison, we need your permission'
					/>
				</Title>
			</Header>
			<PointList>
				<PointItem>
					<IconPrivacy />
					<FormattedMessage
						id='opportunityCarInsurance.consent.stepOne'
						defaultMessage='You consent to sharing your personal information and details about your car insurance once for the purpose of calculating a savings.'
					/>
				</PointItem>
				<PointItem>
					<IconCancel />
					<FormattedMessage
						id='opportunityCarInsurance.consent.stepTwo'
						defaultMessage='Your data will only be used for making this comparison. After 4 weeks, your data will be automatically deleted.'
					/>
				</PointItem>
			</PointList>
			<ButtonGroup column left>
				<ButtonLink
					label={
						<FormattedMessage
							id='opportunityCarInsurance.consent.labelReadMore'
							defaultMessage='Read more about data processing'
						/>
					}
					tracking={{
						type: 'textlink',
						label: 'Read more about data processing',
					}}
					onClick={() =>
						onShowDataProcessing(
							selectedInsurer,
							personalDetails,
							licensePlate,
							damageFreeYears,
							drivenDistancePerYear,
							currentCoverage,
						)
					}
				/>
				<ButtonLink
					label={
						<FormattedMessage
							id='opportunityCarInsurance.consent.labelPrivacyPolicy'
							defaultMessage='Want to know more?'
						/>
					}
					subtitle={
						<FormattedMessage
							id='opportunityCarInsurance.consent.subtitlePrivacyPolicy'
							defaultMessage='Read our privacy statement'
						/>
					}
					aria-label={`
							${formatMessage({ id: 'opportunityCarInsurance.consent.labelPrivacyPolicy', defaultMessage: 'Want to know more?' })}.
							${formatMessage({ id: 'opportunityCarInsurance.consent.subtitlePrivacyPolicy', defaultMessage: 'Read our privacy statement' })}.
							${formatMessage({ id: 'buttonExternal.annotation', defaultMessage: 'You are leaving the app and will be taken to the browser.' })}
						`}
					icon={<IconLink />}
					onClick={() => {
						onLogScopedEvent('PRIVACY_STATEMENT')
						onOpenUrl('https://www.abnamro.nl/nl/prive/abnamro/privacy/index.html')
					}}
				/>
			</ButtonGroup>
			<ButtonGroup spacing={8} center>
				{onStepBack && (
					<ButtonText
						label={<FormattedMessage id='opportunityCarInsurance.buttonBack' defaultMessage='Back' />}
						onClick={() => onStepBack()}
					/>
				)}
				<ButtonSolid
					label={
						<FormattedMessage
							id='opportunityCarInsurance.consent.buttonGivePermission'
							defaultMessage='I give permission'
						/>
					}
					onClick={() =>
						onShowComparison(
							selectedInsurer,
							personalDetails,
							licensePlate,
							damageFreeYears,
							drivenDistancePerYear,
							currentCoverage,
						)
					}
				/>
			</ButtonGroup>
		</Card>
	)
}
